import type { JSX } from 'react'
import { SUPPLY_COLORS } from './supply-labels'

interface SupplyIconProps {
  size?: number
}

// Traced from the design system icons plus-sign-square, minus-sign-square and analytics-01
// (Figma 8616-4102 and 8616-4168): the frame is a squircle, not a rounded rectangle.
const SQUIRCLE_FRAME =
  'M1.66667 8C1.66667 5.01444 1.66667 3.52166 2.59416 2.59416C3.52166 1.66667 5.01444 1.66667 8 1.66667C10.9855 1.66667 12.4783 1.66667 13.4059 2.59416C14.3333 3.52166 14.3333 5.01444 14.3333 8C14.3333 10.9855 14.3333 12.4783 13.4059 13.4059C12.4783 14.3333 10.9855 14.3333 8 14.3333C5.01444 14.3333 3.52166 14.3333 2.59416 13.4059C1.66667 12.4783 1.66667 10.9855 1.66667 8Z'

function SquircleIcon({ children }: { children: JSX.Element }): JSX.Element {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={SQUIRCLE_FRAME} />
      {children}
    </svg>
  )
}

export function PlusSquareIcon(): JSX.Element {
  return (
    <SquircleIcon>
      <path d="M8 5.33333V10.6667M10.6667 8H5.33333" strokeLinecap="round" />
    </SquircleIcon>
  )
}

export function MinusSquareIcon(): JSX.Element {
  return (
    <SquircleIcon>
      <path d="M10.6667 8H5.33333" strokeLinecap="round" />
    </SquircleIcon>
  )
}

// Chart markers, unlike the boxed icons the legend uses: the maquette draws them as bare shapes.
export function DeliveryMarker({ size = 14 }: SupplyIconProps): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 14 14"
      fill={SUPPLY_COLORS.cream}
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <rect x="2.76" y="2.76" width="8.49" height="8.49" rx="1.5" transform="rotate(45 7 7)" />
    </svg>
  )
}

export function OrderMarker({ size = 14 }: SupplyIconProps): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 14 14"
      fill={SUPPLY_COLORS.cream}
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <circle cx="7" cy="7" r="4" />
    </svg>
  )
}

export function StockoutIcon({ size = 14 }: SupplyIconProps): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeWidth={0.875}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6.41667 12.8333C5.93938 12.8333 5.48344 12.6407 4.57154 12.2555C2.30162 11.2967 1.16667 10.8172 1.16667 10.0108C1.16667 9.78495 1.16667 5.87096 1.16667 4.08333M6.41667 12.8333V6.62363M6.41667 12.8333C6.77641 12.8333 7.07 12.7239 7.58333 12.505M11.6667 4.08333V7" />
      <path d="M9.33333 8.75L11.0833 10.5M11.0833 10.5L12.8333 12.25M11.0833 10.5L9.33333 12.25M11.0833 10.5L12.8333 8.75" />
      <path d="M4.27345 5.6533L2.56942 4.82875C1.63425 4.37622 1.16667 4.14997 1.16667 3.79167C1.16667 3.43337 1.63425 3.20711 2.56942 2.75459L4.27345 1.93003C5.32515 1.42112 5.85101 1.16667 6.41667 1.16667C6.98232 1.16667 7.5082 1.42112 8.55989 1.93003L10.2639 2.75459C11.1991 3.20711 11.6667 3.43337 11.6667 3.79167C11.6667 4.14997 11.1991 4.37622 10.2639 4.82875L8.55989 5.6533C7.5082 6.16222 6.98232 6.41667 6.41667 6.41667C5.85101 6.41667 5.32515 6.16222 4.27345 5.6533Z" />
      <path d="M2.91667 7L4.08333 7.58333" />
      <path d="M9.33333 2.33333L3.5 5.25" />
    </svg>
  )
}

export function BreakageIcon({ size = 14 }: SupplyIconProps): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.125}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1.75 2.91667H4.91728C5.30737 2.91667 5.67163 2.72172 5.88799 2.39715L6.36201 1.68618C6.57837 1.36162 6.94266 1.16667 7.33273 1.16667H9.95079C10.4529 1.16667 10.8988 1.488 11.0575 1.9644L11.375 2.91667M12.25 2.91667H4.66667" />
      <path d="M11.375 2.91667L11.0135 8.69517C11.001 8.88609 10.9892 9.06412 10.9775 9.23037M2.625 2.91667L2.97769 8.68968C3.06801 10.0979 3.11318 10.802 3.48756 11.3083C3.67267 11.5587 3.91113 11.77 4.18778 11.9288C4.74734 12.25 5.49682 12.25 6.99574 12.25H8.45833" />
      <path d="M11.6667 11.0833C11.6667 10.1168 10.8832 9.33333 9.91667 9.33333C8.95014 9.33333 8.16667 10.1168 8.16667 11.0833C8.16667 12.0499 8.95014 12.8333 9.91667 12.8333C10.8832 12.8333 11.6667 12.0499 11.6667 11.0833Z" />
    </svg>
  )
}

export function DeliveryIcon({ size = 14 }: SupplyIconProps): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 10.6667 10.6667"
      fill="none"
      stroke="currentColor"
      strokeWidth={0.666_667}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M7.55523 8.88878C8.04615 8.88878 8.44412 8.49081 8.44412 7.99989C8.44412 7.50897 8.04615 7.111 7.55523 7.111C7.06431 7.111 6.66634 7.50897 6.66634 7.99989C6.66634 8.49081 7.06431 8.88878 7.55523 8.88878Z" />
      <path d="M3.11057 8.88878C3.60149 8.88878 3.99946 8.49081 3.99946 7.99989C3.99946 7.50897 3.60149 7.111 3.11057 7.111C2.61965 7.111 2.22168 7.50897 2.22168 7.99989C2.22168 8.49081 2.61965 8.88878 3.11057 8.88878Z" />
      <path d="M2.22168 7.98762C1.73425 7.96336 1.43017 7.89082 1.21378 7.67447C0.997395 7.45807 0.924889 7.15398 0.900591 6.66656M3.99946 7.99989H6.66612M8.4439 7.98762C8.93132 7.96336 9.23541 7.89082 9.45181 7.67447C9.77723 7.349 9.77723 6.82522 9.77723 5.77767V4.88878H7.68835C7.35746 4.88878 7.19199 4.88878 7.05812 4.84527C6.7875 4.75736 6.57532 4.54518 6.48741 4.27457C6.4439 4.14068 6.4439 3.97523 6.4439 3.64434C6.4439 3.14799 6.4439 2.89982 6.37866 2.69898C6.24675 2.29306 5.92852 1.97482 5.52261 1.84293C5.32177 1.77767 5.07359 1.77767 4.57724 1.77767H0.888346" />
      <path d="M0.888346 3.55501H3.55501" />
      <path d="M0.888346 4.88835H2.66612" />
      <path d="M6.44434 2.66634H7.25376C7.9006 2.66634 8.22398 2.66634 8.48718 2.82355C8.75038 2.98075 8.90371 3.2655 9.21038 3.83501L9.77767 4.88856" />
    </svg>
  )
}

export function OrderIcon({ size = 14 }: SupplyIconProps): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 10.6667 10.6667"
      fill="none"
      stroke="currentColor"
      strokeWidth={0.666_667}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3.58569 1.21077L3.50924 1.27141C3.42693 1.33669 3.38577 1.36934 3.3448 1.38991C3.20299 1.46112 3.03362 1.45182 2.90062 1.36552C2.86219 1.34059 2.82492 1.30364 2.75038 1.22974C2.57258 1.05347 2.48368 0.965328 2.41668 0.932982C2.17689 0.81723 1.88898 0.935462 1.80198 1.18541C1.77767 1.25526 1.77767 1.37991 1.77767 1.62919V9.19857C1.77767 9.3127 1.77767 9.36977 1.78282 9.40301C1.83346 9.73004 2.216 9.88715 2.48504 9.69141C2.51239 9.6715 2.55311 9.63115 2.63451 9.55043C2.68571 9.49968 2.71135 9.47426 2.73562 9.45435C2.96097 9.26959 3.28146 9.25199 3.52601 9.41097C3.55235 9.42808 3.58063 9.45052 3.6372 9.49537L3.69771 9.54337C3.80009 9.62457 3.85129 9.66519 3.90258 9.69297C4.10172 9.80079 4.3425 9.80079 4.54167 9.69297C4.59296 9.66519 4.64411 9.62457 4.74651 9.54337L4.77767 9.51866C4.90967 9.41399 4.97567 9.36163 5.04362 9.32915C5.2266 9.24163 5.43985 9.24163 5.62282 9.32915C5.69078 9.36163 5.75678 9.41399 5.88878 9.51866L5.91994 9.54337C6.02234 9.62457 6.07349 9.66519 6.12478 9.69297C6.32394 9.80079 6.56474 9.80079 6.76389 9.69297C6.81518 9.66519 6.86638 9.62457 6.96874 9.54337L7.02922 9.49537C7.0858 9.45052 7.11411 9.42808 7.14043 9.41097C7.385 9.25199 7.70549 9.26959 7.93083 9.45435C7.95509 9.47426 7.98074 9.49968 8.03194 9.55043C8.11336 9.63115 8.15407 9.6715 8.1814 9.69141C8.45047 9.88715 8.833 9.73004 8.88362 9.40301C8.88878 9.36977 8.88878 9.3127 8.88878 9.19857V1.62919C8.88878 1.37991 8.88878 1.25526 8.86447 1.18541C8.77745 0.935462 8.48958 0.81723 8.24976 0.932982C8.18278 0.965328 8.09385 1.05347 7.91607 1.22974C7.84154 1.30364 7.80425 1.34059 7.76585 1.36552C7.63282 1.45182 7.46345 1.46112 7.32162 1.38991C7.28069 1.36934 7.23954 1.33669 7.15722 1.27141L7.08078 1.21077C6.87162 1.04489 6.76705 0.961955 6.653 0.924693C6.51749 0.880408 6.37118 0.880408 6.23567 0.924693C6.12163 0.961955 6.01705 1.0449 5.80789 1.21077L5.77767 1.23476C5.61891 1.36067 5.53954 1.42363 5.45149 1.44772C5.37411 1.46891 5.29234 1.46891 5.21496 1.44772C5.12691 1.42363 5.04754 1.36067 4.88878 1.23476L4.85856 1.21077C4.6494 1.04489 4.54482 0.961955 4.43079 0.924693C4.29526 0.880408 4.14897 0.880408 4.01343 0.924693C3.8994 0.961955 3.79483 1.04489 3.58569 1.21077Z" />
      <path d="M3.55501 5.33301H7.11057" />
      <path d="M3.55501 3.55501H5.33279" />
      <path d="M3.55501 7.111H7.11057" />
    </svg>
  )
}
